/**
 * Paradise Health — Shopify 2.0 Theme Global JavaScript
 * Handles: mobile menu, sticky header, quantity selectors,
 * share buttons, cart interactions
 */

/* ── Custom Elements ── */

/**
 * Sticky Header Web Component
 */
class StickyHeader extends HTMLElement {
  constructor() {
    super();
    this.header = this.querySelector('.header');
    this.onScrollHandler = this.onScroll.bind(this);
    this.currentScrollTop = 0;
  }

  connectedCallback() {
    window.addEventListener('scroll', this.onScrollHandler, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  onScroll() {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;

    if (scrollTop > 100) {
      this.classList.add('header-wrapper--scrolled');
    } else {
      this.classList.remove('header-wrapper--scrolled');
    }

    this.currentScrollTop = scrollTop;
  }
}

if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', StickyHeader);
}

/**
 * Mobile Menu Web Component
 */
class MobileMenu extends HTMLElement {
  constructor() {
    super();
    this.toggleButtons = document.querySelectorAll('[data-mobile-menu-toggle]');
    this.closeButtons = this.querySelectorAll('[data-mobile-menu-close]');
    this.drawer = this.querySelector('.mobile-menu__drawer');
  }

  connectedCallback() {
    this.toggleButtons.forEach((btn) => {
      btn.addEventListener('click', () => this.toggle());
    });

    this.closeButtons.forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.getAttribute('aria-hidden') === 'false') {
        this.close();
      }
    });
  }

  toggle() {
    const isHidden = this.getAttribute('aria-hidden') !== 'false';
    if (isHidden) {
      this.open();
    } else {
      this.close();
    }
  }

  open() {
    this.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Focus the first link
    requestAnimationFrame(() => {
      const firstLink = this.querySelector('.mobile-menu__link');
      if (firstLink) firstLink.focus();
    });
  }

  close() {
    this.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    // Return focus to toggle button
    const toggleBtn = document.querySelector('[data-mobile-menu-toggle]');
    if (toggleBtn) toggleBtn.focus();
  }
}

if (!customElements.get('mobile-menu')) {
  customElements.define('mobile-menu', MobileMenu);
}

/**
 * Predictive Search Web Component
 */
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('[data-predictive-search-input]');
    this.resultsContainer = this.querySelector('[data-predictive-search-results]');
    this.toggleButtons = document.querySelectorAll('[data-search-toggle]');
    this.closeButtons = this.querySelectorAll('[data-search-close]');
    this.abortController = null;
  }

  connectedCallback() {
    this.toggleButtons.forEach((btn) => {
      btn.addEventListener('click', () => this.toggle());
    });

    this.closeButtons.forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });

    if (this.input) {
      this.input.addEventListener(
        'input',
        debounce((e) => this.onInput(e), 300)
      );
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.getAttribute('aria-hidden') === 'false') {
        this.close();
      }
    });
  }

  toggle() {
    const isHidden = this.getAttribute('aria-hidden') !== 'false';
    if (isHidden) {
      this.open();
    } else {
      this.close();
    }
  }

  open() {
    this.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => {
      if (this.input) this.input.focus();
    });
  }

  close() {
    this.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (this.resultsContainer) this.resultsContainer.innerHTML = '';
  }

  async onInput(event) {
    const searchTerm = event.target.value.trim();

    if (!searchTerm || searchTerm.length < 2) {
      if (this.resultsContainer) this.resultsContainer.innerHTML = '';
      return;
    }

    // Abort previous request
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    try {
      const response = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(searchTerm)}&resources[type]=product,article,page&resources[limit]=6`,
        { signal: this.abortController.signal }
      );

      if (!response.ok) return;
      const data = await response.json();
      this.renderResults(data, searchTerm);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Predictive search error:', error);
      }
    }
  }

  renderResults(data, searchTerm) {
    if (!this.resultsContainer) return;

    const resources = data.resources?.results || {};
    const products = resources.products || [];
    const articles = resources.articles || [];
    const pages = resources.pages || [];

    if (products.length === 0 && articles.length === 0 && pages.length === 0) {
      this.resultsContainer.innerHTML = `<p style="padding: 2rem; text-align: center; opacity: 0.5;">No results found for "${searchTerm}"</p>`;
      return;
    }

    let html = '<div style="padding: 1.5rem; max-width: 600px; margin: 0 auto;">';

    if (products.length > 0) {
      html += '<h3 style="font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.1em; opacity: 0.5; margin-bottom: 1rem;">Products</h3>';
      products.forEach((product) => {
        html += `<a href="${product.url}" style="display: flex; align-items: center; gap: 1rem; padding: 0.75rem; border-radius: 8px; text-decoration: none; color: inherit; margin-bottom: 0.25rem;">`;
        if (product.image) {
          html += `<img src="${product.image}" alt="${product.title}" style="width: 48px; height: 48px; object-fit: cover; border-radius: 6px;" loading="lazy">`;
        }
        html += `<div><div style="font-weight: 500;">${product.title}</div><div style="font-size: 0.875rem; opacity: 0.6;">${product.price}</div></div></a>`;
      });
    }

    if (articles.length > 0) {
      html += '<h3 style="font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.1em; opacity: 0.5; margin: 1.5rem 0 1rem;">Articles</h3>';
      articles.forEach((article) => {
        html += `<a href="${article.url}" style="display: block; padding: 0.75rem; border-radius: 8px; text-decoration: none; color: inherit; margin-bottom: 0.25rem;"><div style="font-weight: 500;">${article.title}</div></a>`;
      });
    }

    if (pages.length > 0) {
      html += '<h3 style="font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.1em; opacity: 0.5; margin: 1.5rem 0 1rem;">Pages</h3>';
      pages.forEach((page) => {
        html += `<a href="${page.url}" style="display: block; padding: 0.75rem; border-radius: 8px; text-decoration: none; color: inherit; margin-bottom: 0.25rem;"><div style="font-weight: 500;">${page.title}</div></a>`;
      });
    }

    html += '</div>';
    this.resultsContainer.innerHTML = html;
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}

/* ── Quantity Selectors ── */
document.addEventListener('click', (e) => {
  const minusBtn = e.target.closest('[data-quantity-minus]');
  const plusBtn = e.target.closest('[data-quantity-plus]');

  if (minusBtn) {
    const input = minusBtn.parentElement.querySelector('input[type="number"]');
    if (input) {
      const min = parseInt(input.min) || 0;
      const current = parseInt(input.value) || 0;
      if (current > min) {
        input.value = current - 1;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  if (plusBtn) {
    const input = plusBtn.parentElement.querySelector('input[type="number"]');
    if (input) {
      const max = parseInt(input.max) || Infinity;
      const current = parseInt(input.value) || 0;
      if (current < max) {
        input.value = current + 1;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }
});

/* ── Share Button ── */
document.addEventListener('click', (e) => {
  const shareToggle = e.target.closest('[data-share-toggle]');
  if (shareToggle) {
    const dropdown = shareToggle.parentElement.querySelector('[data-share-dropdown]');
    if (dropdown) {
      dropdown.hidden = !dropdown.hidden;
    }
    return;
  }

  const copyLink = e.target.closest('[data-copy-link]');
  if (copyLink) {
    const url = copyLink.getAttribute('data-copy-link');
    navigator.clipboard.writeText(url).then(() => {
      const originalText = copyLink.textContent;
      copyLink.textContent = 'Copied!';
      setTimeout(() => {
        copyLink.textContent = originalText;
      }, 2000);
    });
    return;
  }

  // Close share dropdowns when clicking outside
  document.querySelectorAll('[data-share-dropdown]').forEach((d) => {
    if (!d.closest('[data-share-button]')?.contains(e.target)) {
      d.hidden = true;
    }
  });
});

/* ── Collection Sort ── */
document.addEventListener('change', (e) => {
  const sortSelect = e.target.closest('[data-sort-by]');
  if (sortSelect) {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sortSelect.value);
    window.location.href = url.toString();
  }
});

/* ── Variant Picker ── */
document.addEventListener('change', (e) => {
  const optionInput = e.target.closest('[data-option-input]');
  if (optionInput) {
    // Update selected state visually
    const fieldset = optionInput.closest('.product__option');
    if (fieldset) {
      fieldset.querySelectorAll('.product__option-value').forEach((label) => {
        label.classList.remove('product__option-value--selected');
      });
      optionInput.closest('.product__option-value')?.classList.add('product__option-value--selected');
    }
  }
});

/* ── Utility: Debounce ── */
function debounce(fn, wait) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), wait);
  };
}

/* ── Sticky Header (via data-sticky attribute) ── */
(function initStickyHeader() {
  var wrapper = document.querySelector('[data-sticky="true"]');
  if (!wrapper) return;

  var onScroll = function() {
    if (window.scrollY > 100) {
      wrapper.classList.add('header-wrapper--scrolled');
    } else {
      wrapper.classList.remove('header-wrapper--scrolled');
    }
  };

  window.addEventListener('scroll', onScroll, { passive: true });
})();

/* ── Scroll Animations (IntersectionObserver) ── */
function initScrollAnimations() {
  var elements = document.querySelectorAll('.fade-up:not(.is-visible), .fade-in:not(.is-visible)');
  if (!elements.length) return;

  var observer = new IntersectionObserver(
    function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
  );

  elements.forEach(function(el) { observer.observe(el); });
}

initScrollAnimations();

/* ── Shopify Theme Editor: Section Lifecycle ── */
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function() {
    initScrollAnimations();
  });
}

